'use client'

import { useEffect } from 'react'
import { Button } from './Button'

/**
 * Ce que voit quelqu'un quand un écran tombe.
 *
 * ## ⚠️ Pourquoi il existe : l'écran par défaut est inutilisable
 *
 * Sans limite d'erreur, Next rend sa propre page — fond noir, texte anglais,
 * « This page couldn't load ». Constaté le 2026-09-02 sur l'Oasis d'un
 * apprenant : aucun repère Umiros, aucune indication de ce qu'il faut faire,
 * et rien qui dise que ses données sont intactes. Pour quelqu'un qui découvre
 * son espace la semaine de l'ouverture, c'est indiscernable d'un compte cassé.
 *
 * ## ⚠️ Le cas le plus fréquent n'est pas une panne
 *
 * `Failed to find Server Action` arrive dès qu'une page reste ouverte pendant
 * un déploiement : l'identifiant interne des actions change à chaque
 * construction, et le bouton d'une page ancienne pointe vers une adresse
 * disparue. Rien n'est cassé, rien n'est perdu — il faut recharger. Ce cas est
 * donc **détecté et nommé**, avec la phrase qui va bien, plutôt que noyé dans un
 * « une erreur est survenue » qui ferait appeler le support pour rien.
 *
 * ⚠️ **`reset()` ne suffit pas pour ce cas-là** : il rejoue le rendu avec le
 * même paquet JavaScript périmé, donc la même erreur. Le bouton recharge
 * franchement la page.
 */
export function EcranErreur({
  error,
  reset,
  espace = 'votre espace',
}: {
  error: Error & { digest?: string }
  reset: () => void
  espace?: string
}) {
  useEffect(() => {
    /* Le journal du navigateur garde la trace : `digest` est la seule chose qui
       relie ce que voit la personne à la ligne côté serveur. */
    console.error('Écran en erreur', error)
  }, [error])

  const perimee = /Server Action|older or newer deployment/i.test(error.message)

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 px-6 text-center">
      <div className="flex max-w-[46ch] flex-col gap-2.5">
        <h1 className="text-[22px] font-semibold text-ink">
          {perimee ? 'Cette page a été mise à jour' : 'Cet écran n’a pas pu s’afficher'}
        </h1>
        <p className="text-[14.5px] leading-[1.55] text-ink-soft">
          {perimee
            ? 'Une nouvelle version vient d’être publiée pendant que vous étiez sur cette page. Rechargez pour continuer — rien de ce que vous avez fait n’est perdu.'
            : `Le problème vient de notre côté, pas du vôtre : ${espace} et vos données sont intacts. Réessayez dans un instant, et si cela persiste, signalez-le avec le bouton d’aide.`}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button type="button" onClick={() => window.location.reload()}>
          Recharger la page
        </Button>
        {/* `reset()` n'est proposé que quand il a une chance de servir : sur une
            action périmée, il rejouerait le même code périmé. */}
        {!perimee && (
          <Button type="button" variant="ghost" onClick={reset}>
            Réessayer
          </Button>
        )}
      </div>

      {error.digest && (
        <p className="text-[12px] text-ink-soft">
          Référence à nous transmettre : <span className="font-mono">{error.digest}</span>
        </p>
      )}
    </div>
  )
}
